import React, { useEffect, useState } from 'react';
import { Linking, Platform } from 'react-native';
import { WebLinking } from '@alucio/core';
import { DNABox, DNAButton, DNAText } from '@alucio/lux-ui';
import { UserRole } from '@alucio/aws-beacon-amplify/src/models';
import { useCurrentUser } from 'src/state/redux/selector/user';
import { usePublisherVersioningState } from 'src/state/machines/publisherVersioning/PublisherVersioningProvider';
import { getVaultDocumentUrl } from './getVaultDocumentUrl';
import { useVaultIntegrationDomain } from './useVaultIntegrationDomain';

const OpenInVaultButton: React.FC = () => {
  const { userProfile } = useCurrentUser();
  const { documentORM, currentDocumentVersionORM } = usePublisherVersioningState();
  const document = documentORM.model;
  const tenantId = document.tenantId;
  const version = currentDocumentVersionORM?.model;
  const integrationId = document.integration?.integrationId;
  const eligible = userProfile?.role === UserRole.TENANT_PUBLISHER && userProfile.tenantId === tenantId &&
    document.integrationType === 'VEEVA' && version?.integrationType === 'VEEVA' &&
    !!integrationId && !!version.integration?.externalVersionId;
  const { domain, hasError, retry } = useVaultIntegrationDomain({ tenantId, integrationId, enabled: eligible });
  const [openFailed, setOpenFailed] = useState(false);

  useEffect(() => {
    setOpenFailed(false);
  }, [eligible, tenantId, integrationId, document.id, version?.id]);

  if (!eligible || !integrationId) return null;
  if (hasError) {
    return (
      <DNAButton
        appearance="outline"
        status="tertiary"
        onPress={retry}
      >
        Retry Vault link
      </DNAButton>
    );
  }
  const url = getVaultDocumentUrl(version, domain);
  if (!url) return null;

  const openVault = async () => {
    setOpenFailed(false);
    try {
      if (Platform.OS === 'web') await WebLinking.openURL(url);
      else await Linking.openURL(url);
    } catch {
      setOpenFailed(true);
    }
  };

  return (
    <DNABox appearance="col" spacing="xs">
      <DNAButton testID="open-in-vault-button" appearance="outline" status="tertiary" onPress={openVault}>
        Open in Vault
      </DNAButton>
      {openFailed && <DNAText>Could not open Vault. Please try again.</DNAText>}
    </DNABox>
  );
};

export default OpenInVaultButton;
